/**
 * Primary hover/active generation from the brand base color.
 * Lightness deltas match `_theme-variables.scss` (--theme-primary → hover/active).
 * Keep FALLBACK_* in sync with those CSS custom properties.
 */

export const PRIMARY_HOVER_LIGHTNESS_DELTA_FALLBACK = 4.118;
export const PRIMARY_ACTIVE_LIGHTNESS_DELTA_FALLBACK = 8.039;

function parseCssNumber(raw: string | undefined | null, fallback: number): number {
  const n = parseFloat(String(raw ?? '').trim());
  return Number.isFinite(n) ? n : fallback;
}

export function getPrimaryHoverLightnessDelta(doc: Document | null): number {
  if (!doc?.documentElement) {
    return PRIMARY_HOVER_LIGHTNESS_DELTA_FALLBACK;
  }
  const v = doc.defaultView?.getComputedStyle(doc.documentElement).getPropertyValue('--theme-primary-hover-lightness-delta');
  return parseCssNumber(v, PRIMARY_HOVER_LIGHTNESS_DELTA_FALLBACK);
}

export function getPrimaryActiveLightnessDelta(doc: Document | null): number {
  if (!doc?.documentElement) {
    return PRIMARY_ACTIVE_LIGHTNESS_DELTA_FALLBACK;
  }
  const v = doc.defaultView?.getComputedStyle(doc.documentElement).getPropertyValue('--theme-primary-active-lightness-delta');
  return parseCssNumber(v, PRIMARY_ACTIVE_LIGHTNESS_DELTA_FALLBACK);
}

function rgbToHsl(r: number, g: number, b: number): [number, number, number] {
  r /= 255;
  g /= 255;
  b /= 255;

  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  let h = 0;
  let s = 0;
  const l = (max + min) / 2;

  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);

    switch (max) {
      case r:
        h = (g - b) / d + (g < b ? 6 : 0);
        break;
      case g:
        h = (b - r) / d + 2;
        break;
      case b:
        h = (r - g) / d + 4;
        break;
    }
    h /= 6;
  }

  return [h * 360, s * 100, l * 100];
}

function hslToHex(h: number, s: number, l: number): string {
  h /= 360;
  s /= 100;
  l /= 100;

  const hue2rgb = (p: number, q: number, t: number) => {
    if (t < 0) t += 1;
    if (t > 1) t -= 1;
    if (t < 1 / 6) return p + (q - p) * 6 * t;
    if (t < 1 / 2) return q;
    if (t < 2 / 3) return p + (q - p) * (2 / 3 - t) * 6;
    return p;
  };

  let r: number;
  let g: number;
  let b: number;

  if (s === 0) {
    r = g = b = l;
  } else {
    const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
    const p = 2 * l - q;
    r = hue2rgb(p, q, h + 1 / 3);
    g = hue2rgb(p, q, h);
    b = hue2rgb(p, q, h - 1 / 3);
  }

  const toHex = (c: number) => {
    const hex = Math.round(c * 255).toString(16);
    return hex.length === 1 ? `0${hex}` : hex;
  };

  return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
}

function adjustHexLightness(color: string, amount: number): string {
  const hex = color.replace('#', '');
  const r = parseInt(hex.slice(0, 2), 16);
  const g = parseInt(hex.slice(2, 4), 16);
  const b = parseInt(hex.slice(4, 6), 16);
  const hsl = rgbToHsl(r, g, b);
  hsl[2] = Math.max(0, Math.min(100, hsl[2] + amount));
  return hslToHex(hsl[0], hsl[1], hsl[2]);
}

/** Hover tint: lighter than base (Figma primary scale). */
export function generatePrimaryHoverColor(baseColor: string, doc: Document | null): string {
  return adjustHexLightness(baseColor, getPrimaryHoverLightnessDelta(doc));
}

/** Active tint: lighter than base (Figma primary scale). */
export function generatePrimaryActiveColor(baseColor: string, doc: Document | null): string {
  return adjustHexLightness(baseColor, getPrimaryActiveLightnessDelta(doc));
}
